import type {TFunction, i18n as I18n} from "i18next";
import type {ButtonDropdownProps} from "@awsui/components-react/button-dropdown";
import type {TopNavigationProps} from "@awsui/components-react/top-navigation";
import {SUPPORTED_LANGUAGES, SupportedLanguage} from "i18n";


// Material Design "globe" / "language" icon, embedded as a data URL so the
// trigger stays language-agnostic (no flag) and renders identically on every
// browser. currentColor inherits the TopNavigation foreground color so it
// adapts to dark mode automatically.
const LANGUAGE_ICON_SVG: string =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
        "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>" +
            "<circle cx='12' cy='12' r='10'/>" +
            "<path d='M2 12h20'/>" +
            "<path d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z'/>" +
        "</svg>"
    );

// flagcdn.com only serves national flags, so we draw the Ikurriña (Basque) and
// the Senyera (Catalan) as inline SVG data URLs. Keeping them inline avoids
// any network dependency and renders identically across platforms.
const inlineSvgDataUrl = (svg: string): string =>
    `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

// Ikurriña: red field, white cross over a green saltire.
const BASQUE_FLAG_SVG = inlineSvgDataUrl(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 20'>" +
        "<rect width='30' height='20' fill='#D52B1E'/>" +
        "<path d='M0 0 L30 20 M30 0 L0 20' stroke='#009B48' stroke-width='4'/>" +
        "<path d='M15 0 V20 M0 10 H30' stroke='#FFFFFF' stroke-width='4'/>" +
    "</svg>"
);

// Senyera: 9 horizontal stripes (5 yellow, 4 red) — drawn as a yellow
// background with 4 red stripes overlaid at fixed offsets.
const CATALAN_FLAG_SVG = inlineSvgDataUrl(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 90 60'>" +
        "<rect width='90' height='60' fill='#FCDD09'/>" +
        "<rect y='6.67'  width='90' height='6.67' fill='#DA121A'/>" +
        "<rect y='20'    width='90' height='6.67' fill='#DA121A'/>" +
        "<rect y='33.33' width='90' height='6.67' fill='#DA121A'/>" +
        "<rect y='46.67' width='90' height='6.67' fill='#DA121A'/>" +
    "</svg>"
);

// English is represented with the GB flag (flagcdn has no "language" endpoint
// and showing the country most associated with the language is the convention).
const FLAG_URL_BY_LANGUAGE: Record<SupportedLanguage, string> = {
    es: "https://flagcdn.com/40x30/es.png",
    en: "https://flagcdn.com/40x30/gb.png",
    eu: BASQUE_FLAG_SVG,
    ca: CATALAN_FLAG_SVG,
};

export const getFlagUrlForLanguage = (code: SupportedLanguage): string =>
    FLAG_URL_BY_LANGUAGE[code];

export const resolveSupportedLanguage = (raw: string | undefined): SupportedLanguage => {
    if (!raw) return "es";
    const base = raw.split("-")[0] as SupportedLanguage;
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? base : "es";
};

/**
 * Build the "languages" menu-dropdown utility for the top navigation bar.
 * Renders as an icon-only trigger (the flag of the active language) so it
 * stays compact, with a dropdown listing all four languages.
 *
 * The click handler is wired directly on the utility (TopNavigation does not
 * expose a top-level onUtilityClick).
 */
export const buildLanguagePickerUtility = (
    t: TFunction,
    i18n: I18n,
    currentLanguage: SupportedLanguage
): TopNavigationProps.MenuDropdownUtility => ({
    type: "menu-dropdown",
    ariaLabel: t("prefs:language"),
    iconUrl: LANGUAGE_ICON_SVG,
    onItemClick: ({detail}) => {
        if (detail.id.startsWith("lang-")) {
            i18n.changeLanguage(detail.id.replace(/^lang-/, ""));
        }
    },
    items: SUPPORTED_LANGUAGES.map((code: SupportedLanguage): ButtonDropdownProps.Item => ({
        id: `lang-${code}`,
        text: t(`prefs:languages.${code}`),
        iconUrl: getFlagUrlForLanguage(code),
        ...(code === currentLanguage ? {iconName: "check"} : {}),
    })),
});
